import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Input,
  Chip,
  Pagination,
  Card,
  CardBody,
  useDisclosure,
  Skeleton,
  Tooltip,
  addToast,
  CardHeader,
  Divider,
  Spinner,
} from "@heroui/react";
import {
  Plus,
  Mail,
  Calendar,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  Ban,
  Search,
} from "lucide-react";
import { useHttpRequest } from "../../../hooks/useHttpRequest";
import {
  getInvitedMembers,
  inviteMembers,
  cancelInviteMember,
} from "../../../api/user";
import type { InviteMember } from "../../../api/user.type";
import dayjs from "dayjs";

const inviteSearchSchema = z.object({
  page: z.number().min(1).catch(1),
  size: z.number().min(5).max(100).catch(10),
  email: z.string().optional(),
});

export const Route = createFileRoute("/(authenticated)/invite/")({
  component: RouteComponent,
  validateSearch: inviteSearchSchema,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { page, size, email } = Route.useSearch();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const [inviteEmails, setInviteEmails] = useState<string[]>([""]);
  const [searchInput, setSearchInput] = useState(email || "");

  const {
    data: inviteData,
    request: fetchInvites,
    isLoading,
    isInit,
    error,
  } = useHttpRequest(() => getInvitedMembers({ page, size, email }));

  const { request: sendInvites, isLoading: isSending } = useHttpRequest(
    (payload?: string[]) => inviteMembers({ emails: payload || [] })
  );

  const { request: cancelInvite, isLoading: isCanceling } = useHttpRequest(
    (payload?: number) => cancelInviteMember(payload as number)
  );

  const totalCount = inviteData?.totalCount || 0;
  const totalPages = inviteData?.totalPage || 0;
  const invites = inviteData?.data || [];

  useEffect(() => {
    fetchInvites();
  }, [page, size, email]);

  const handlePageChange = (newPage: number) => {
    navigate({
      search: { page: newPage, size, email },
    });
  };

  const handleSearch = () => {
    navigate({
      search: { page: 1, size, email: searchInput || undefined },
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    navigate({
      search: { page: 1, size, email: undefined },
    });
  };

  const handleAddEmailField = () => {
    setInviteEmails([...inviteEmails, ""]);
  };

  const handleRemoveEmailField = (index: number) => {
    if (inviteEmails.length > 1) {
      setInviteEmails(inviteEmails.filter((_, i) => i !== index));
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...inviteEmails];
    newEmails[index] = value;
    setInviteEmails(newEmails);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inviteEmails[index].trim() && index === inviteEmails.length - 1) {
        handleAddEmailField();
      }
    }
  };

  const handleSendInvites = async () => {
    const validEmails = inviteEmails.filter(
      (email) => email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    );

    if (validEmails.length === 0) {
      addToast({
        title: "유효한 이메일을 입력해주세요.",
        color: "danger",
      });
      return;
    }

    try {
      await sendInvites({
        payload: validEmails,
      });
      addToast({
        title: "초대장 발송 완료",
        description: `${validEmails.length}명에게 초대장을 발송했습니다.`,
        color: "success",
      });
      setInviteEmails([""]);
      onOpenChange();
      fetchInvites();
    } catch (error) {
      addToast({
        title: "초대장 발송 실패",
        description: "초대장 발송에 실패했습니다. 다시 시도해주세요.",
        color: "danger",
      });
    }
  };

  const handleCancelInvite = async (inviteId: number) => {
    try {
      await cancelInvite({ payload: inviteId });
      addToast({
        title: "초대 취소 완료",
        description: "초대를 취소했습니다.",
        color: "success",
      });
      fetchInvites();
    } catch (error) {
      addToast({
        title: "초대 취소 실패",
        description: "초대 취소에 실패했습니다. 다시 시도해주세요.",
        color: "danger",
      });
    }
  };

  const getStatusChip = (status: InviteMember["status"]) => {
    const statusConfig = {
      INVITING: { color: "default" as const, icon: Clock, text: "초대중" },
      INVITED: { color: "success" as const, icon: Mail, text: "초대완료" },
      EXPIRED: { color: "danger" as const, icon: AlertCircle, text: "만료됨" },
      CANCELED: { color: "danger" as const, icon: Ban, text: "취소됨" },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <Chip
        color={config.color}
        variant="dot"
        startContent={<Icon className="h-3 w-3" />}
        size="sm"
      >
        {config.text}
      </Chip>
    );
  };

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardBody className="pt-6">
            <div className="text-center text-danger">
              초대 목록을 불러오는 중 오류가 발생했습니다.
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 space-y-6">
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <h1 className="text-2xl font-bold">멤버 초대 관리</h1>
      </div>

      {isLoading && !isInit ? (
        <div className="flex items-center justify-center py-4">
          <Spinner size="lg" color="primary" />
        </div>
      ) : (
        <>
          <Card shadow="sm">
            <CardHeader className="pb-3">
              <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 w-full">
                <div className="flex items-center space-x-2">
                  <Chip size="lg" variant="flat">
                    총 {totalCount}개
                  </Chip>
                </div>
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                  <Input
                    placeholder="이메일로 검색..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    startContent={
                      <Search className="h-4 w-4 text-default-400" />
                    }
                    isClearable
                    onClear={() => setSearchInput("")}
                    className="w-full sm:w-64"
                    variant="bordered"
                  />
                  <div className="flex space-x-2">
                    <Button
                      color="primary"
                      onPress={handleSearch}
                      size="md"
                      className="flex-1 sm:flex-none"
                    >
                      검색
                    </Button>
                    {email && (
                      <Button
                        color="default"
                        variant="bordered"
                        onPress={handleClearSearch}
                        size="md"
                        className="flex-1 sm:flex-none"
                      >
                        초기화
                      </Button>
                    )}
                    <Button
                      color="primary"
                      startContent={<Plus className="h-4 w-4" />}
                      onPress={onOpen}
                      size="md"
                      className="flex-1 sm:flex-none"
                    >
                      <span className="hidden sm:inline">멤버 초대</span>
                      <span className="sm:hidden">초대</span>
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <Divider />
            <CardBody className="p-0">
              <Table
                aria-label="초대 멤버 테이블"
                classNames={{
                  td: "border-b border-divider/50",
                }}
                shadow="none"
              >
                <TableHeader>
                  <TableColumn>이메일</TableColumn>
                  <TableColumn className="hidden sm:table-cell">
                    상태
                  </TableColumn>
                  <TableColumn className="hidden md:table-cell">
                    만료일
                  </TableColumn>
                  <TableColumn className="hidden lg:table-cell">
                    대상 정보
                  </TableColumn>
                  <TableColumn align="end">액션</TableColumn>
                </TableHeader>
                <TableBody emptyContent="초대된 멤버가 없습니다.">
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Mail className="h-4 w-4 text-default-400" />
                          <div className="min-w-0">
                            <div className="font-medium">{invite.email}</div>
                            <div className="text-sm text-default-500 sm:hidden">
                              {getStatusChip(invite.status)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {getStatusChip(invite.status)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center space-x-1 text-sm">
                          <Calendar className="h-3 w-3 text-default-400" />
                          <span>
                            {dayjs(invite.expiredAt).format("YYYY.MM.DD HH:mm")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="min-w-0">
                          <div className="text-sm font-medium">
                            {invite.userName || "-"}
                          </div>
                          <div className="text-xs text-default-500 truncate">
                            {invite.userEmail || "-"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {invite.status === "INVITING" && (
                          <Button
                            color="danger"
                            variant="solid"
                            size="sm"
                            onPress={() => handleCancelInvite(invite.id)}
                            isLoading={isCanceling}
                            startContent={
                              !isCanceling && <X className="h-3 w-3" />
                            }
                          >
                            <span className="hidden sm:inline">취소</span>
                          </Button>
                        )}
                        {invite.uid && (
                          <Button
                            variant="solid"
                            size="sm"
                            onPress={() =>
                              navigate({
                                to: "/users/$id",
                                params: { id: invite.uid?.toString() || "" },
                              })
                            }
                          >
                            유저 보기
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                total={totalPages}
                page={page}
                onChange={handlePageChange}
                showControls
                showShadow
                color="primary"
              />
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-xl font-semibold">멤버 초대하기</h3>
                <p className="text-small text-default-600">
                  초대할 멤버의 이메일 주소를 입력하세요.
                </p>
              </ModalHeader>
              <ModalBody>
                <Form className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-small font-medium">
                        이메일 주소
                      </span>
                      <span className="text-tiny text-default-400">
                        {inviteEmails.filter((email) => email.trim()).length}개
                        입력됨
                      </span>
                    </div>

                    {inviteEmails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="email"
                          placeholder="example@company.com"
                          value={email}
                          onValueChange={(value) =>
                            handleEmailChange(index, value)
                          }
                          onKeyDown={(e) => handleEmailKeyDown(e, index)}
                          startContent={
                            <Mail size={16} className="text-default-400" />
                          }
                          variant="bordered"
                          className="flex-1"
                          description={
                            index === inviteEmails.length - 1 && email.trim()
                              ? "엔터키를 누르면 새 입력창이 추가됩니다"
                              : undefined
                          }
                        />
                        {inviteEmails.length > 1 && (
                          <Button
                            isIconOnly
                            variant="light"
                            color="danger"
                            onPress={() => handleRemoveEmailField(index)}
                          >
                            <X size={16} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="bordered"
                    startContent={<Plus size={16} />}
                    onPress={handleAddEmailField}
                    className="w-full"
                  >
                    이메일 추가
                  </Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  취소
                </Button>
                <Button
                  color="primary"
                  onPress={handleSendInvites}
                  isLoading={isSending}
                  isDisabled={
                    inviteEmails.filter(
                      (email) =>
                        email.trim() &&
                        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
                    ).length === 0
                  }
                  startContent={!isSending && <CheckCircle2 size={16} />}
                >
                  {(() => {
                    const validCount = inviteEmails.filter(
                      (email) =>
                        email.trim() &&
                        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
                    ).length;
                    return validCount > 0
                      ? `${validCount}명에게 초대장 발송`
                      : "초대장 발송";
                  })()}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
